import { Mail } from "lucide-react";
import { ReactNode } from "react";
import { Control } from "react-hook-form";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "../../../../components/ui/common/form";
import { Input } from "../../../../components/ui/common/input";
import { getCompanyName } from "../../../../utils/cacheStorage";
import { customEmailValidation } from "../../../../utils/utils";
import { RoleSelectionPolicy } from "../services/userRolePolicy";
import { UserFormData, UserRoleOption } from "../types/user.types";
import UserFormLayout, { UserSection } from "./UserFormLayout";
import UserRoleSelector from "./UserRoleSelector";

interface UserFormFieldsProps {
  control: Control<UserFormData>;
  roles: UserRoleOption[];
  rolePolicy: RoleSelectionPolicy;
  /** The profile-picture card; Register and Edit each supply their own (local file vs persisted picture). */
  avatarSlot: ReactNode;
  /** Makes the email read-only (e.g. when a user edits their own profile). */
  disableEmail?: boolean;
}

/**
 * The user form body shared by the Register and Edit drawers, in the order of
 * the design: Profile picture → User details → Access level. Same fields and
 * validation as the legacy modals (first/last name and email required, phone
 * optional); the company is shown read-only, and the role choice is delegated
 * to the injected `RoleSelectionPolicy`.
 */
const UserFormFields = ({
  control,
  roles,
  rolePolicy,
  avatarSlot,
  disableEmail
}: UserFormFieldsProps) => (
  <UserFormLayout>
    <UserSection title="Profile picture">{avatarSlot}</UserSection>

    <UserSection title="User details">
      <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
        <FormInput
          control={control}
          name="firstName"
          label="First name"
          required
          errorMessage="Please enter first name"
          rules={{ validate: v => !!v?.trim() || "Please enter first name" }}
        />
        <FormInput
          control={control}
          name="lastName"
          label="Last name"
          required
          errorMessage="Please enter last name"
          rules={{ validate: v => !!v?.trim() || "Please enter last name" }}
        />
      </div>
      <FormField
        control={control}
        name="email"
        rules={{ required: "Please enter email", validate: customEmailValidation }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Email<span className="tw-ml-0.5 tw-text-destructive">*</span>
            </FormLabel>
            <div className="tw-relative">
              <Mail className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-h-4 tw-w-4 tw--translate-y-1/2 tw-text-muted-foreground" />
              <FormControl>
                <Input {...field} type="email" className="tw-pl-9" disabled={disableEmail} />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
        <FormInput control={control} name="phone" label="Phone" />
        <div className="tw-space-y-2">
          <p className="tw-text-sm tw-font-medium tw-text-foreground">Company</p>
          <Input value={getCompanyName() ?? ""} readOnly disabled aria-label="Company" />
        </div>
      </div>
    </UserSection>

    <UserSection title="Access level">
      <UserRoleSelector
        control={control}
        roles={rolePolicy.visibleRoles(roles)}
        disabled={rolePolicy.isLocked}
      />
      {rolePolicy.isLocked && (
        <p className="tw-border-t tw-border-dashed tw-border-border tw-pt-5 tw-text-base tw-text-muted-foreground">
          Role changes are locked after registration. Create a corrected account if the selected
          access level is wrong.
        </p>
      )}
    </UserSection>
  </UserFormLayout>
);

export default UserFormFields;
